import { UseQueryOptions, useMutation, useQuery } from "@tanstack/react-query";
import { useCompanyContext } from "@/features/company/hooks/use-company-context";
import { QueryOptionsFactory } from "@/features/company/utils";
import type {
  levelBronzeFormDataResponseType,
  levelBronzeFormDataType
} from "@/features/flow/types";
import type { AddressType } from "@/features/user/types";
import { useAppDispatch } from "@/redux/hooks";
import { setIsPrepareSessionError } from "@/redux/slices/kycSession";
import { pushOtherToEnd } from "@/utils/array.utils";
import { IdentityDocumentType } from "@/views/identity/components/level-card-status-container/verified-company-form/types";
import { KYCApiClient } from "../api-client/api-client";
import {
  type CreateKYCDocument,
  type CreateKYCSession,
  isPrepareKYCSessionError,
  type KYCSession
} from "../types";

export type useKYCProps = {
  address: AddressType;
};

export const useKYC = () => {
  const dispatch = useAppDispatch();
  const { leUuid } = useCompanyContext();

  const KYCStateQuery = (UUID: string | null) =>
    useQuery({
      queryKey: ["kyc", "state", UUID],
      queryFn: () => (UUID ? KYCApiClient.getKYCSessionState({ UUID }) : null),
      enabled: !!UUID,
      refetchOnWindowFocus: false,
      refetchOnMount: false
    });

  const KYCDocumentsQuery = (UUID: string | null) =>
    useQuery({
      queryKey: UUID ? ["kyc", "documents", UUID] : ["kyc", "documents"],
      queryFn: () =>
        UUID && leUuid
          ? KYCApiClient.getKYCDocuments({
              UUID,
              contextLegalEntityUuid: leUuid === null ? undefined : leUuid
            })
          : null,
      enabled: !!UUID
    });

  const getKycBronzeFormQuery = (enable?: boolean) =>
    useQuery({
      queryKey: ["kycBronzeForm"],
      queryFn: () => KYCApiClient.getKycBronzeForm(),
      enabled: enable ?? true
    });

  const KYCDocumentTypesQuery = () =>
    useQuery({
      queryKey: ["kyc", "documents", "types"],
      queryFn: () => KYCApiClient.getKYCDocumentTypes()
    });

  const allDocumentTypesQuery = (
    options?: UseQueryOptions<IdentityDocumentType[], null, IdentityDocumentType[]>
  ) =>
    useQuery<IdentityDocumentType[], null, IdentityDocumentType[]>({
      queryKey: options?.queryKey ?? ["documents", "types", "all"],
      queryFn: async () => await KYCApiClient.getAllDocumentTypes(),
      select: (data: IdentityDocumentType[]) => pushOtherToEnd(data, "type_en"),
      refetchOnWindowFocus: false,
      refetchOnMount: false,
      gcTime: Infinity,
      staleTime: Infinity,
      ...options
    });

  const getAllIdentityDocumentTypesV2Query = () =>
    useQuery({
      ...QueryOptionsFactory.identityDocumentTypeAll(),
      queryFn: async () => {
        const response = await KYCApiClient.getAllIdentityDocumentTypesV2();
        return response.data;
      }
    });

  const createKYCSessionMutation = () =>
    useMutation<KYCSession | null, Error, CreateKYCSession>({
      mutationFn: async (data) => {
        if (!data.sessionSenario && !data.sessionType)
          throw Error("createKYCSessionMutation - must define sessionSenario or sessionType");

        const preparation = await KYCApiClient.prepareKYCSession(data);
        if (isPrepareKYCSessionError(preparation)) {
          dispatch(setIsPrepareSessionError(true));
          return null;
        }

        let KYCSession = await KYCApiClient.createKYCSession({
          ...data,
          sessionId: preparation.session_id,
          AbstractEntity: data.UUID
        });
        if (!KYCSession?.sessionId && data.sessionType) {
          KYCSession = {
            sessionId: preparation.session_id,
            statusType: "UNFINISHED",
            sessionType: data.sessionType,
            AbstractEntity: data.UUID // NaturalPerson UUID
          };
        }

        return KYCSession;
      }
    });

  const editKYCSessionMutation = () =>
    useMutation<KYCSession, Error, CreateKYCDocument>({
      mutationFn: async (data) => await KYCApiClient.editKYCSession(data)
    });
  const editKycBronzeMutation = () =>
    useMutation<levelBronzeFormDataResponseType, Error, levelBronzeFormDataType>({
      mutationFn: async (data) => await KYCApiClient.editBronzeKYC(data)
    });
  const editKycBronzeMutationWithoutDebounce = () =>
    useMutation<levelBronzeFormDataResponseType, Error, levelBronzeFormDataType>({
      mutationFn: async (data) => await KYCApiClient.editBronzeKYCWithoutDebounce(data)
    });
  const updateDocumentMutation = () =>
    useMutation<KYCSession, Error, CreateKYCDocument>({
      mutationFn: async (data) => await KYCApiClient.updateDocument(data)
    });
  const updateQuizVideoQuestionMutation = () =>
    useMutation<KYCSession, Error, CreateKYCDocument>({
      mutationFn: async (data) => await KYCApiClient.updateQuizVideoQuestion(data)
    });

  return {
    getAllIdentityDocumentTypesV2Query,
    KYCStateQuery,
    KYCDocumentsQuery,
    KYCDocumentTypesQuery,
    allDocumentTypesQuery,
    createKYCSessionMutation,
    updateDocumentMutation,
    editKYCSessionMutation,
    updateQuizVideoQuestionMutation,
    editKycBronzeMutation,
    getKycBronzeFormQuery,
    editKycBronzeMutationWithoutDebounce
  };
};
